import { ISectionLogger } from "@multiweb/logging";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { DeviceInfo } from "../../../../Device/Info/DeviceInfo";
import { ITextBuilder } from "../../../../Global/Base/Builders/ITextBuilder";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { CaseInsensitiveMap } from "../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { Event as GlobalEvent, EventCallback } from "../../../../Global/Base/Events";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { Margin } from "../Helpers/Margin";
import { UIManager } from "../UIManager";
import { ComponentExpander } from "../XGControls/ComponentExpander";
import { Form } from "../XGControls/Form";
import { AlignType } from "./ControlEnums";
import { ControlWithLoggerParams, IMaxLength } from "./ControlInterfaces";
import { EditableControl } from "./EditableControl";

export class Memo extends EditableControl implements IMaxLength {
    //#region Fields
    public alignType: AlignType;
    public override innerElement: HTMLTextAreaElement = document.createElement("textarea");

    private static _codeMirrorLoadedModules: CaseInsensitiveMap<Array<HTMLScriptElement | HTMLLinkElement>> = new CaseInsensitiveMap<Array<HTMLScriptElement | HTMLLinkElement>>();
    private static _codeMirrorLoadedModulesPromises: CaseInsensitiveMap<Promise<any>> = new CaseInsensitiveMap<Promise<any>>();

    private _maxLength: number; //max. delka
    private _codeMirror: CodeMirror.EditorFromTextArea;
    private readonly _syntaxHighlight: boolean = false;
    private readonly _syntaxHighlightLang: string = "";
    private _counterElement: HTMLLabelElement = document.createElement("label");
    private _counter: number = 0; //pocet znaku
    private _isMaxLenNull: boolean;
    private _formLayoutChanged: GlobalEvent<Memo, any>;
    private readonly _logger: ISectionLogger;
    private _windowResizedListener: EventCallback;
    private _darkTheme: boolean;
    private _formChangeLayoutListener: EventCallback<any, any>;
    private _componentExpander: ComponentExpander;
    private _themeChangeObserver: MutationObserver;

    public get maxLength(): number {
        return this._maxLength;
    }

    public set maxLength(value: number) {
        if (!this._isMaxLenNull) {
            if (this._maxLength >= value)
                this._maxLength = value;
        } else {
            if (value)
                this._maxLength = value;
        }
        if (this._isMaxLenNull && this._maxLength === 0) {
            this.innerElement.removeAttribute("max-length");
        } else {
            this.innerElement.maxLength = this._maxLength;
        }
        this.updateCounterElement();
    }

    public override get displayedValue(): ITextBuilder {
        return this.innerElement.value;
    }

    public override set displayedValue(v: ITextBuilder) {
        this.innerElement.value = v.toString();
    }
    //#endregion Properties

    //#endregion Fields

    //#region Constructors
    constructor(params: ControlWithLoggerParams) {
        super(params);
        const { logger, element } = params;
        this._logger = logger;
        this._initializeComponent(element);
        this._addClass();

        this.element.appendChild(this._counterElement);
        this.element.appendChild(this.innerElement);
        this._setPlaceholder();
        this.updateCounterElement();
        this.alignType = Memo._setAlign(this.element.getAttribute("data-align"));

        // timeout tu je, protoze se z nejakeho duvodu meni ID objektu, takze se potom styly vytvori na spatne ID
        self.setTimeout(() => this._setComponentParamByAlign(), 0);
        //this.Element.setAttribute("data-syntaxHighlight", "true");
        //this.Element.setAttribute("data-syntaxLanguage", "xml");

        this._syntaxHighlight = DataConverter.toBoolean(this.element.getAttribute("data-syntaxHighlight"), false);
        this._syntaxHighlightLang = this.element.getAttribute("data-syntaxLanguage");
        this._darkTheme = Memo._isThemeDark();

        this._setCaptionReference(this.innerElement);
        this._registerThemeChangeObserver();
        this._loadCodeMirror();
        this._setMemoExpandable();
    }
    //#endregion Constructors

    //#region Methods

    private _setMemoExpandable(): void {
        this._componentExpander = new ComponentExpander(this);
        this._componentExpander.onButtonShow = () => {
            return this.isValueLoaded;
        };
    }

    private _setPlaceholder(): void {
        if (this.innerElement && this._placeholderLocKey) {
            Localization.addLocTxtData(this.innerElement, this._placeholderLocKey, "placeholder");
        }
    }

    private async _loadCodeMirror(): Promise<void> {
        if (this._syntaxHighlight) {
            await this._loadLanguage(this._syntaxHighlightLang);
        }
    }

    private _registerThemeChangeObserver(): void {
        this._themeChangeObserver = new MutationObserver((mutations: MutationRecord[]) => {
            const currentDarkTheme = Memo._isThemeDark();
            mutations.forEach((mutation) => {
                if (mutation.attributeName === "class" && currentDarkTheme !== this._darkTheme) {
                    this._darkTheme = currentDarkTheme;
                    this._reloadTheme();
                }
            });
        });

        this._themeChangeObserver.observe(document.documentElement, { attributes: true });
    }

    private static _isThemeDark(): boolean {
        return document.documentElement.classList.contains("theme-dark");
    }

    private static async _loadCodeMirrorFiles(modules: Array<string> = []): Promise<void> {
        await Memo._coderMirrorRegisterModule("basic", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/lib/codemirror.js" } }) as HTMLScriptElement,
            createElement({ type: "link", attrs: { "href": "css/codemirror.css", "rel": "stylesheet" } }) as HTMLLinkElement,
            createElement({ type: "link", attrs: { "href":  "css/CodeMirror/theme/base16-light.css", "rel": "stylesheet" } }) as HTMLLinkElement,
            createElement({ type: "link", attrs: { "href":  "css/CodeMirror/theme/ambiance.css", "rel": "stylesheet" } }) as HTMLLinkElement,
            createElement({ type: "link", attrs: { "href":  "css/CodeMirror/theme/material.css", "rel": "stylesheet" } }) as HTMLLinkElement,
        ]);

        await Memo._coderMirrorRegisterModule("folding", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/fold/xml-fold.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/fold/foldgutter.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/fold/foldcode.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/fold/brace-fold.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/fold/indent-fold.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/fold/comment-fold.js" } }) as HTMLScriptElement,
            createElement({ type: "link", attrs: { "href": "css/CodeMirror/addon/fold/foldgutter.css", "rel": "stylesheet" } }) as HTMLLinkElement,
        ]);

        await Memo._coderMirrorRegisterModule("closetag", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/edit/closetag.js" } }) as HTMLScriptElement,
        ]);

        await Memo._coderMirrorRegisterModule("formatting", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/edit/formatting.js" } }) as HTMLScriptElement,
        ]);

        await Memo._coderMirrorRegisterModule("closebrackets", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/edit/closebrackets.js" } }) as HTMLScriptElement,
        ]);

        await Memo._coderMirrorRegisterModule("matchbrackets", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/edit/matchbrackets.js" } }) as HTMLScriptElement,
        ]);

        await Memo._coderMirrorRegisterModule("matchtags", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/edit/matchtags.js" } }) as HTMLScriptElement,
        ]);

        await Memo._coderMirrorRegisterModule("matchhighlight", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/scroll/annotatescrollbar.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/search/matchesonscrollbar.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/search/searchcursor.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/search/match-highlighter.js" } }) as HTMLScriptElement,
        ]);

        await Memo._coderMirrorRegisterModule("fullscreen", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/display/fullScreen.js" } }) as HTMLScriptElement,
            createElement({ type: "link", attrs: { "href": "css/CodeMirror/addon/display/fullscreen.css", "rel": "stylesheet" } }) as HTMLLinkElement,
        ]);

        await Memo._coderMirrorRegisterModule("search", modules, [
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/dialog/dialog.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/search/searchcursor.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/search/search.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/scroll/annotatescrollbar.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/search/matchesonscrollbar.js" } }) as HTMLScriptElement,
            createElement({ type: "script", attrs: { "src": "js/CodeMirror/addon/search/jump-to-line.js" } }) as HTMLScriptElement,
            createElement({ type: "link", attrs: { "href": "css/CodeMirror/addon/dialog/dialog.css", "rel": "stylesheet" } }) as HTMLLinkElement,
            createElement({ type: "link", attrs: { "href": "css/CodeMirror/addon/search/matchesonscrollbar.css", "rel": "stylesheet" } }) as HTMLLinkElement,
        ]);
    }

    private static async _coderMirrorRegisterModule(moduleName: string, modules: Array<string> = [], files: Array<HTMLScriptElement | HTMLLinkElement> = []): Promise<void> {
        if ((modules.indexOf(moduleName) !== -1 || moduleName === "basic" || moduleName.startsWith("lang_")) && !Memo._codeMirrorLoadedModules.has(moduleName)) {
            Memo._codeMirrorLoadedModules.set(moduleName, files);
            Memo._codeMirrorLoadedModulesPromises.set(moduleName, Memo._loadSrcFile(Memo._codeMirrorLoadedModules.get(moduleName)));
            await Memo._codeMirrorLoadedModulesPromises.get(moduleName);
        } else
            await Memo._codeMirrorLoadedModulesPromises.get(moduleName);
    }

    private static async _loadLangScriptFile(lang: string): Promise<boolean> {
        await Memo._coderMirrorRegisterModule(`lang_${lang}`, [], [createElement({ type: "script", attrs: { "src": `js/CodeMirror/mode/${lang}/${lang}.js` } }) as HTMLScriptElement]);
        return true;
    }

    private async _loadLanguage(lang: string): Promise<void> {
        if (!lang)
            lang = "";

        let configCodeMirror: CodeMirror.EditorConfiguration;
        let additionalLang: string[] = [];

        switch (lang.toLowerCase()) {
            case "xml":
                await Memo._loadCodeMirrorFiles(["folding", "closetag", "matchtags", "search", "fullscreen", "formatting"]);
                configCodeMirror = {
                    mode: "application/xml",
                    lineNumbers: true,
                    lineWrapping: false,
                    foldGutter: true,
                    matchTags: { bothTags: true },
                    autoCloseTags: true,
                    extraKeys: { "Ctrl-J": "toMatchingTag", "Alt-F": "findPersistent" },
                    gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
                } as CodeMirror.EditorConfiguration;
                break;
            case "html":
                await Memo._loadCodeMirrorFiles(["folding", "closetag", "matchtags", "formatting", "fullscreen"]);
                additionalLang = ["css", "javascript", "xml", "vbscript"];
                lang = "htmlmixed";
                configCodeMirror = {
                    mode: "text/html",
                    lineNumbers: true,
                    lineWrapping: false,
                    foldGutter: true,
                    matchTags: { bothTags: true },
                    autoCloseTags: true,
                    extraKeys: { "Ctrl-J": "toMatchingTag" },
                    gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
                } as CodeMirror.EditorConfiguration;
                break;
            case "sql":
                await Memo._loadCodeMirrorFiles(["closebrackets", "matchbrackets", "matchhighlight", "fullscreen"]);
                configCodeMirror = {
                    mode: "sql",
                    lineNumbers: true,
                    lineWrapping: true,
                    autoCloseBrackets: true,
                    matchBrackets: true,
                    highlightSelectionMatches: { showToken: /\w/, annotateScrollbar: true },
                } as CodeMirror.EditorConfiguration;
                break;
            default:
                await Memo._loadCodeMirrorFiles(["folding", "closebrackets", "matchbrackets", "matchhighlight", "fullscreen", "formatting"]);
                configCodeMirror = {
                    lineNumbers: true,
                    lineWrapping: true,
                    autoCloseBrackets: true,
                    matchBrackets: true,
                    highlightSelectionMatches: { showToken: /\w/, annotateScrollbar: true },
                } as CodeMirror.EditorConfiguration;

                if (lang) {
                    const modLang = Memo._getModeLang(lang);
                    lang = modLang.lang;
                    configCodeMirror["mode"] = modLang.mode;
                }
        }
        if (!configCodeMirror.extraKeys)
            configCodeMirror.extraKeys = {};
        configCodeMirror.extraKeys["F11"] = (cm: any) => {
            cm.setOption("fullScreen", !cm.getOption("fullScreen"));
        };
        configCodeMirror.extraKeys["Esc"] = (cm: any) => {
            if (cm.getOption("fullScreen"))
                cm.setOption("fullScreen", false);
        };
        configCodeMirror.extraKeys["Shift-Ctrl-L"] = (cm: any) => {
            this._logger.log(cm.getOption("lineWrapping"));
            cm.setOption("lineWrapping", !cm.getOption("lineWrapping"));
        };
        configCodeMirror.extraKeys["Shift-Ctrl-C"] = (cm: any) => {
            cm.commentRange(true, cm.getCursor(true), cm.getCursor(false));
        };
        configCodeMirror.extraKeys["Shift-Ctrl-U"] = (cm: any) => {
            cm.commentRange(false, cm.getCursor(true), cm.getCursor(false));
        };
        configCodeMirror.extraKeys["Shift-Ctrl-F"] = (cm: any) => {
            const lastCursor = cm.getCursor(true);
            cm.execCommand("selectAll");
            cm.autoFormatRange(cm.getCursor(true), cm.getCursor(false));
            cm.setCursor(lastCursor);
        };
        //configCodeMirror.extraKeys["Shift-Ctrl-I"] = (cm) => { cm.autoIndentRange(0, (cm as CodeMirror.Editor).getValue().length); }

        if (lang)
            await Memo._loadLangScriptFile(lang);
        if (additionalLang && additionalLang.length > 0)
            for (const lng of additionalLang)
                await Memo._loadLangScriptFile(lng);

        this._codeMirror = CodeMirror.fromTextArea(this.innerElement, configCodeMirror);

        let edited = false;
        this._codeMirror.on("change",
            (_cm, editorChanges) => {
                if (["+input", "+delete", "paste", "cut", "undo", "redo"].some(item => item === editorChanges.origin))
                    edited = true;
                this.updateCounterElement();
            });

        this._codeMirror.on("mousedown",
            (_cm: CodeMirror.Editor, _e: MouseEvent) => {
                if (!this._isEditing)
                    this.clicked.trigger(this, undefined);
            });

        this._codeMirror.on("blur",
            () => {
                if (edited)
                    this.valueChanged.trigger(this, { Sender: this });

                edited = false;
            });

        this._windowResizedListener = () => this._codeMirror.refresh();
        UIManager.instance.windowResized.add(this._windowResizedListener);
        Form.formLayoutChanged.add(this._windowResizedListener);
        this._setEnabledInput(this.isEditing);
        this.isEditingChanged.add(() => {
            this._setEnabledInput(this._isEditing);
        });
    }

    protected override _setEnabledInput(value: boolean): void {
        if (this._codeMirror) {
            this._codeMirror.setOption("readOnly", !value);
            this._reloadTheme();
        }
        this.innerElement.readOnly = !value;
    }

    private _reloadTheme(): void {
        this._codeMirror.setOption("theme", this._darkTheme ?
            (this.isEditing ? "material" : "ambiance") :
            (this.isEditing ? "default" : "base16-light"));
    }

    /**
     * Nektere jazyky v sobe maji jine integrovane jen jsou jako jiny mod, tato metoda se stara o naplneni spravnych hodnot
     * @param lang
     */
    private static _getModeLang(lang: string): { mode: string; lang: string } {
        switch (lang.toLowerCase()) {
            case "typescript":
                return { mode: "text/typescript", lang: "javascript" };
            case "less":
                return { mode: "text/x-less", lang: "css" };
            case "c":
                return { mode: "text/x-csrc", lang: "clike" };
            case "cpp":
                return { mode: "text/x-c++src", lang: "clike" };
            case "csharp":
                return { mode: "text/x-csharp", lang: "clike" };
            case "java":
                return { mode: "text/x-java", lang: "clike" };
            case "objectivec":
                return { mode: "text/x-objectivec", lang: "clike" };
            case "scala":
                return { mode: "text/x-scala", lang: "clike" };
            case "ceylon":
                return { mode: "text/x-ceylon", lang: "clike" };
            case "sql":
                return { mode: "text/x-sql", lang: "sql" };
            case "mysql":
                return { mode: "text/x-mysql", lang: "sql" };
            case "mariadb":
                return { mode: "text/x-mariadbl", lang: "sql" };
            case "cassandra":
                return { mode: "text/x-cassandra", lang: "sql" };
            case "plsql":
                return { mode: "text/x-plsql", lang: "sql" };
            case "mssql":
                return { mode: "text/x-mssql", lang: "sql" };
            case "hive":
                return { mode: "text/x-hive", lang: "sql" };
            case "pgsql":
                return { mode: "text/x-pgsql", lang: "sql" };
            case "gql":
                return { mode: "text/x-gql", lang: "sql" };
            case "json":
                return { mode: "application/ld+json", lang: "javascript" };
            default:
                return { mode: lang, lang: lang };
        }
    }

    private static _loadSrcFile(files: Array<HTMLScriptElement | HTMLLinkElement> = []): Promise<boolean> {
        return new Promise<boolean>((r) => {
            let loaded = 0;

            if (files.length === 0)
                r(true);

            for (const file of files) {
                file.addEventListener("load", () => {
                    if (++loaded === files.length)
                        r(true);
                });
                file.addEventListener("error", () => {
                    file.remove();
                    if (++loaded === files.length)
                        r(false);
                });
                document.head.appendChild(file);
            }
        });
    }

    /**
     * Nastaveni hodnoty mema
     * @param value nastavovana hodnota
     */
    public setControlValue(value: any): void {
        this.innerElement.textContent = value;
        this.updateCounterElement();
    }

    private _setComponentParamByAlign(): void {
        const id = this.element.id;
        const positionStyles = [];
        const width = this.getNumDataAttr("width");
        const height = this.getNumDataAttr("height");
        //const top = this.getNumDataAttr('top');
        //const left = this.getNumDataAttr('left');
        const margin = Margin.parse(this.getDataAttr("margin"));

        switch (this.alignType) {
            case AlignType.left:
            case AlignType.right:
                if (id !== undefined && id !== null)
                    positionStyles.push("#" + id + " { width: " + width + "px; }");
                break;
            case AlignType.top:
            case AlignType.bottom:
                if (id !== undefined && id !== null)
                    positionStyles.push("#" + id + " { height: " + height + "px; width: 100%; }");
                break;
            case AlignType.client:
                if (id !== undefined && id !== null && !this._codeMirror) {
                    const heightMargin = margin.top + margin.bottom;
                    positionStyles.push("#" + id + " { height: calc(100% - " + heightMargin + "px); width: 100%; }");
                }
                break;
        }

        let styles = document.querySelector("style");
        if (styles == null) {
            styles = document.createElement("style");
            styles.textContent = "";
            document.head.appendChild(styles);
        }
        const style = styles.textContent;
        styles.textContent = style + positionStyles.join("\n");
    }

    /**
     * Vraceni hodnoty mema
     */
    public getControlValue(): any {
        if (this._codeMirror)
            return this._codeMirror.getValue();
        return this.innerElement.textContent;
    }

    /**
     * Vraceni hodnoty mema
     */
    public override get controlValue(): string {
        if (this._codeMirror)
            return this._codeMirror.getValue();
        return this.innerElement.value;
    }

    /**
     * Nastaveni hodnoty
     */
    public override set controlValue(val: string) {
        if (val) {
            this.innerElement.value = val;
            if (this._codeMirror) {
                this._codeMirror.setValue(val);
                this._codeMirror.getDoc().clearHistory();
            }
        } else {
            this.innerElement.value = "";
            if (this._codeMirror) {
                this._codeMirror.setValue("");
                this._codeMirror.getDoc().clearHistory();
            }
        }
        this.updateCounterElement();
        this.element.style.minHeight = this.element.getAttribute("data-height") + "px"; //aby se u mobilnich zarizeni vzdy Memo stahlo zpet na zakladni vysku
    }

    public override focus(options?: FocusOptions): void {
        if (this._codeMirror) {
            this._codeMirror.focus();
            return;
        }
        this.innerElement.focus(options);
    }

    private _addClass(): void {
        this.element.classList.add("Memo");
        this._counterElement.classList.add("MemoCounter");
        this._counterElement.classList.add("Caption");
    }

    /**
     * Inicializace komponenty -> nacitaji a nastavuji se parametry z definice komponenty
     */
    private _initializeComponent(element: HTMLElement): void {
        this.attrName = element.getAttribute("data-attrname");
        if (element.hasAttribute("data-maxLength")) {
            this._isMaxLenNull = false;
            this._maxLength = +element.getAttribute("data-maxLength");
            this.innerElement.maxLength = this._maxLength;
        } else {
            this._isMaxLenNull = true;
            this._maxLength = 0;
        }
        if (element.hasAttribute("data-language"))
            this.innerElement.setAttribute("lang", element.getAttribute("data-language")); //nastaveni jazyka pro kontrolu pravopisu language/data-lang
        this._addListeners();
    }

    /**
     * update pocitadla
     */
    public updateCounterElement(): void {
        this._counter = this.innerElement.value.length;
        this._counterElement.textContent = this._counter + (this._maxLength === 0 && this._isMaxLenNull ? "" : "/" + this._maxLength);
    }

    /** Jestli je formular ve fixnim zobrazeni */
    private _formInFixedLayout(): boolean {
        return $(this.element).parents(".AlignPanel.counted")[0].classList.contains("fits");
    }

    /**
     * Zvetseni this.Element podle vysky textarey
     */
    public resizeElement(): void {
        // resizuje se jenom kdyz je sloupcove zobrazeni
        if (!this._formInFixedLayout()) {
            this.element.style.minHeight = this.element.getAttribute("data-height") + "px";
        } else {
            this.element.style.minHeight = "";
        }
    }

    private static _setAlign(alignAttr: string): AlignType {
        if (!alignAttr)
            return AlignType.none;
        switch (alignAttr.toLocaleLowerCase()) {
            case "alleft":
                return AlignType.left;
            case "alright":
                return AlignType.right;
            case "altop":
                return AlignType.top;
            case "albottom":
                return AlignType.bottom;
            case "alclient":
                return AlignType.client;
            default:
                return AlignType.none;
        }
    }

    /**
     * Nastaveni eventu na zmacknuti klavesy a vlozeni obsahu ze schranky.
     * Kdyz se po stisku tlacitka (nebo po vlozeni obsahu) zmeni pocet zobrazovanych znaku,
     * tak se vyvola event ValueChanged
     */
    private _addListeners(): void {
        this.innerElement.onchange = (_ev: Event) => {
            this.valueChanged.trigger(this, { Sender: this });
        };
        if (DeviceInfo.deviceProperties.desktop) {
            this.innerElement.onkeyup = (e: KeyboardEvent) => {
                EventHelper.stopPropagation(e);
                this.updateCounterElement();
                this._componentExpander.onKeyClicked.trigger(this._componentExpander, e);
            };
            this.innerElement.onpaste = (_e: ClipboardEvent) => {
                self.setTimeout(() => { //zpozdovac, protoze onpaste event se spusti driv nez se data fyzicky vlozi do textareay
                    this.updateCounterElement();
                }, 5);
            };
        } else { //mobilni platformy
            this.innerElement.onkeyup = (e: KeyboardEvent) => {
                EventHelper.stopPropagation(e);
                this.resizeElement();
                this.updateCounterElement();
                this._componentExpander.onKeyClicked.trigger(this._componentExpander, e);
            };
            this.innerElement.onpaste = (_e: ClipboardEvent) => {
                self.setTimeout(() => { //zpozdovac, protoze onpaste event se spusti driv nez se data fyzicky vlozi do textareay
                    this.resizeElement();
                    this.updateCounterElement();
                }, 5);
            };
            this._formLayoutChanged = new GlobalEvent<Memo, any>();
            this._formLayoutChanged.add(() => {
                this.resizeElement();
            });

            this._formChangeLayoutListener = () => this._formLayoutChanged.trigger(this, null);
            Form.formLayoutChanged.add(this._formChangeLayoutListener);
        }
        this.innerElement.onfocus = (_ev: FocusEvent) => {
            /*removePhoneStyles
            if (DeviceInfo.deviceProperties.phone || DeviceInfo.deviceProperties.tablet)
                this.resizeElement();*/
            this.element.classList.add("focused");
        };
        this.innerElement.onblur = (_ev: FocusEvent) => {
            this.element.classList.remove("focused");
        };
    }

    //destruktor
    public override dispose(): void {
        this._themeChangeObserver?.disconnect();
        this._componentExpander.dispose();
        UIManager.instance.windowResized.remove(this._windowResizedListener);
        this._formLayoutChanged?.removeAll();
        Form.formLayoutChanged.remove(this._windowResizedListener);
        if (this._formChangeLayoutListener) Form.formLayoutChanged.remove(this._formChangeLayoutListener);

        super.dispose();
    }

    //#endregion Methods
}
